import React from 'react';
import { Link } from 'react-router-dom';
import { useDashboardController } from '../../controllers/useDashboardController';
import ConfirmationModal from '../components/ConfirmationModal';
import { formatIndianCurrency } from '../../utils/currency';
import {
  Building2,
  Users,
  MessageSquare,
  Eye,
  Heart,
  CheckCircle,
  Clock,
  Star,
  Activity,
  TrendingUp,
  ChevronRight,
  Phone,
  MessageCircle,
  AlertCircle,
  BarChart3,
  FileText,
  Kanban
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-primary)',
        padding: '10px 14px',
        borderRadius: 'var(--radius-sm)',
        boxShadow: 'var(--shadow-lg)',
        fontSize: '12px',
        zIndex: 50
      }}>
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '6px' }}>{label}</p>
        {payload.map((pld, idx) => (
          <p key={idx} style={{ color: pld.color || 'var(--primary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', margin: '2px 0' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: pld.color || 'var(--primary)', display: 'inline-block' }} />
            {pld.name}: {pld.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const Dashboard = () => {
  const {
    loading,
    stats,
    pendingProperties,
    recentLogs,
    chartsData,
    todaysFollowups = [],
    overdueFollowups = [],
    handleCompleteFollowup,
    confirmOpen,
    setConfirmOpen,
    confirmTitle,
    confirmMessage,
    confirmType,
    handleQuickApprove,
    executeConfirmAction,
    fetchDashboardData
  } = useDashboardController();


  if (loading) {
    return (
      <div className="page-body">
        <div className="skeleton skeleton-title" style={{ width: '200px' }} />
        <div className="skeleton skeleton-text" style={{ width: '350px', marginBottom: '32px' }} />
        <div className="stats-grid">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton" style={{ height: '120px', borderRadius: 'var(--radius-md)' }} />
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginTop: '24px' }}>
          <div className="skeleton" style={{ height: '300px' }} />
          <div className="skeleton" style={{ height: '300px' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="page-body">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 className="page-title">Overview Dashboard</h1>
          <p className="page-subtitle">Real-time statistics and administrative insights.</p>
        </div>
        <button onClick={fetchDashboardData} className="btn btn-secondary">
          Refresh Data
        </button>
      </div>

      {/* 1. Stat Cards Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-title">Total Properties</span>
            <div className="stat-icon"><Building2 size={18} /></div>
          </div>
          <span className="stat-value">{stats.totalProperties}</span>
          <div className="stat-footer">
            <span className="trend-badge trend-up"><TrendingUp size={12} /> +12%</span>
            <span style={{ color: 'var(--text-muted)' }}>all time</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-title">Pending Approval</span>
            <div className="stat-icon" style={{ color: 'var(--warning)' }}><Clock size={18} /></div>
          </div>
          <span className="stat-value" style={{ color: 'var(--warning)' }}>{stats.pendingPropertiesCount}</span>
          <div className="stat-footer">
            <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Requires attention</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-title">Approved List</span>
            <div className="stat-icon" style={{ color: 'var(--success)' }}><CheckCircle size={18} /></div>
          </div>
          <span className="stat-value" style={{ color: 'var(--success)' }}>{stats.approvedProperties}</span>
          <div className="stat-footer">
            <span style={{ color: 'var(--text-muted)' }}>publicly listed</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-title">Total Leads / Enquiries</span>
            <div className="stat-icon"><MessageSquare size={18} /></div>
          </div>
          <span className="stat-value">{stats.totalLeads}</span>
          <div className="stat-footer">
            <span className="trend-badge trend-up"><TrendingUp size={12} /> {stats.todayLeads} new</span>
            <span style={{ color: 'var(--text-muted)' }}>created today</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-title">Platform Users</span>
            <div className="stat-icon"><Users size={18} /></div>
          </div>
          <span className="stat-value">{stats.totalUsers}</span>
          <div className="stat-footer">
            <span className="trend-badge trend-up"><TrendingUp size={12} /> {stats.todayNewUsers} new</span>
            <span style={{ color: 'var(--text-muted)' }}>today</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-title">Views / Interactions</span>
            <div className="stat-icon" style={{ color: 'var(--brand-accent)' }}><Eye size={18} /></div>
          </div>
          <span className="stat-value">{stats.totalViews}</span>
          <div className="stat-footer">
            <span style={{ color: 'var(--text-muted)' }}>cumulative page views</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-title">Favorites / Likes</span>
            <div className="stat-icon" style={{ color: 'var(--danger)' }}><Heart size={18} /></div>
          </div>
          <span className="stat-value" style={{ color: 'var(--danger)' }}>{stats.totalLikes}</span>
          <div className="stat-footer">
            <span style={{ color: 'var(--text-muted)' }}>saved by buyers</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-title">Brokerage Fees Saved</span>
            <div className="stat-icon" style={{ color: '#10b981' }}><Star size={18} /></div>
          </div>
          <span className="stat-value" style={{ fontFamily: 'var(--font-sans)' }}>{stats.brokerageSaved}</span>
          <div className="stat-footer">
            <span style={{ color: 'var(--text-muted)' }}>estimated 2% platform fee saved</span>
          </div>
        </div>
      </div>

      {/* CRM Follow-up Reminders: "Aaj ke follow-ups" & "Overdue" */}
      <div className="card" style={{ marginBottom: '24px', borderLeft: (overdueFollowups.length > 0 ? '4px solid #ef4444' : '4px solid var(--primary)') }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Clock size={16} color="var(--primary)" /> CRM Follow-ups & Scheduled Calls
            </h3>
            {overdueFollowups.length > 0 && (
              <span style={{ backgroundColor: '#fee2e2', color: '#dc2626', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <AlertCircle size={12} /> {overdueFollowups.length} Overdue
              </span>
            )}
            <span style={{ backgroundColor: 'var(--brand-accent-soft)', color: 'var(--brand-accent)', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px' }}>
              {todaysFollowups.length} Today
            </span>
          </div>

          <Link to="/leads" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
            Open Central Leads <ChevronRight size={14} />
          </Link>
        </div>

        {todaysFollowups.length === 0 && overdueFollowups.length === 0 ? (
          <div style={{ padding: '16px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
            ✓ No pending follow-ups for today. All scheduled calls are up to date!
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
            {[...overdueFollowups, ...todaysFollowups].map((item) => (
              <div
                key={item.followup_id}
                style={{
                  backgroundColor: item.is_overdue ? '#fff5f5' : 'var(--bg-secondary)',
                  border: item.is_overdue ? '1px solid #fecaca' : '1px solid var(--border-primary)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '12px 14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)' }}>
                      {item.customer_name}
                    </span>
                    <div style={{ fontSize: '11px', color: item.is_overdue ? '#dc2626' : 'var(--text-secondary)', fontWeight: 600, marginTop: '2px' }}>
                      {item.is_overdue ? '⚠️ Overdue: ' : 'Due: '}
                      {new Date(item.due_date).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleCompleteFollowup(item.followup_id)}
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: '11px', padding: '3px 8px', gap: '4px' }}
                    title="Mark task done"
                  >
                    <CheckCircle size={13} color="#10B981" /> Done
                  </button>
                </div>

                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                  {item.task_title} {item.city ? `• ${item.city}` : ''}
                </div>

                {item.customer_mobile && (
                  <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid var(--border-primary)', paddingTop: '8px', marginTop: '2px' }}>
                    <a
                      href={`tel:${item.customer_mobile}`}
                      className="btn btn-secondary btn-sm"
                      style={{ flex: 1, textDecoration: 'none', justifyContent: 'center', fontSize: '11px', padding: '3px 6px' }}
                    >
                      <Phone size={12} /> Call ({item.customer_mobile})
                    </a>
                    <a
                      href={`https://wa.me/91${item.customer_mobile.replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${item.customer_name}, following up regarding your requirement in ${item.city || 'Ghazipur'}.`)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-sm"
                      style={{ flex: 1, backgroundColor: '#25D366', color: '#fff', textDecoration: 'none', justifyContent: 'center', border: 'none', fontSize: '11px', padding: '3px 6px' }}
                    >
                      <MessageCircle size={12} /> WhatsApp
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CRM Phase 3: Leads & Pipeline Analytics */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BarChart3 size={18} color="var(--primary)" /> CRM Leads & Pipeline Analytics
            </h3>
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>
              Live metrics across website popup leads, property enquiries, and app inquiries.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Link
              to="/crm/reports"
              className="btn btn-secondary btn-sm"
              style={{ gap: '6px', fontSize: '12px', textDecoration: 'none' }}
            >
              <FileText size={14} /> Staff Performance & Reports
            </Link>
            <Link
              to="/leads"
              className="btn btn-primary btn-sm"
              style={{ gap: '6px', fontSize: '12px', textDecoration: 'none' }}
            >
              <Kanban size={14} /> Central Leads ({stats.totalCentralLeads || 0})
            </Link>
          </div>
        </div>

        {/* Lead KPI Counters */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <div style={{ padding: '12px 14px', borderRadius: 'var(--radius-sm)', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Aaj Ki Leads</span>
            <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--primary)', marginTop: '4px' }}>
              {stats.todayLeadsCount || 0}
            </div>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Received today</span>
          </div>

          <div style={{ padding: '12px 14px', borderRadius: 'var(--radius-sm)', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Is Hafte (7 Days)</span>
            <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', marginTop: '4px' }}>
              {stats.weekLeadsCount || 0}
            </div>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Last 7 days</span>
          </div>

          <div style={{ padding: '12px 14px', borderRadius: 'var(--radius-sm)', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Is Mahine (Month)</span>
            <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', marginTop: '4px' }}>
              {stats.monthLeadsCount || 0}
            </div>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Current month</span>
          </div>

          <div style={{ padding: '12px 14px', borderRadius: 'var(--radius-sm)', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: '#059669', textTransform: 'uppercase' }}>Closed Deals (Won)</span>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#059669', marginTop: '4px' }}>
              {stats.wonDealsCount || 0}
            </div>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Successful conversions</span>
          </div>

          <div style={{ padding: '12px 14px', borderRadius: 'var(--radius-sm)', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: '#2563eb', textTransform: 'uppercase' }}>Conversion Rate</span>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#2563eb', marginTop: '4px' }}>
              {stats.crmConversionRate || 0}%
            </div>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Lead-to-deal ratio</span>
          </div>
        </div>

        {/* Status Pipeline Breakdown Bar */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>
            Pipeline Status Breakdown (Status-Wise Count)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px' }}>
            {[
              { label: 'New', count: stats.statusCounts?.['New'] || 0, bg: 'rgba(59, 130, 246, 0.1)', color: '#2563eb' },
              { label: 'Contacted', count: stats.statusCounts?.['Contacted'] || 0, bg: 'rgba(245, 158, 11, 0.12)', color: '#d97706' },
              { label: 'Interested', count: stats.statusCounts?.['Interested'] || 0, bg: 'rgba(217, 119, 6, 0.12)', color: '#b45309' },
              { label: 'Site Visit', count: stats.statusCounts?.['Site Visit'] || 0, bg: 'var(--brand-accent-soft)', color: 'var(--brand-accent)' },
              { label: 'Negotiation', count: stats.statusCounts?.['Negotiation'] || 0, bg: 'rgba(14, 165, 233, 0.12)', color: '#0284c7' },
              { label: 'Closed Won', count: stats.statusCounts?.['Closed Won'] || 0, bg: 'rgba(16, 185, 129, 0.12)', color: '#059669' },
              { label: 'Closed Lost', count: stats.statusCounts?.['Closed Lost'] || 0, bg: 'rgba(100, 116, 139, 0.12)', color: '#64748b' }
            ].map(st => (
              <div
                key={st.label}
                style={{
                  backgroundColor: st.bg,
                  borderRadius: 'var(--radius-sm)',
                  padding: '10px 12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span style={{ fontSize: '12px', fontWeight: 600, color: st.color }}>{st.label}</span>
                <span style={{ fontSize: '15px', fontWeight: 800, color: st.color }}>{st.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CRM Charts: City-Wise & Source-Wise */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', borderTop: '1px solid var(--border-primary)', paddingTop: '16px' }}>
          {/* City-wise Leads Chart */}
          <div>
            <h4 style={{ fontSize: '13px', fontWeight: 700, margin: '0 0 12px 0', color: 'var(--text-primary)' }}>
              City-Wise Leads Distribution
            </h4>
            <div style={{ height: '220px' }}>
              {chartsData.crmCityData?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartsData.crmCityData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" vertical={false} />
                    <XAxis dataKey="name" stroke="var(--text-muted)" fontSize={11} tickLine={false} dy={8} />
                    <YAxis stroke="var(--text-muted)" fontSize={11} tickLine={false} dx={-4} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="Leads" fill="var(--primary)" barSize={24} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
                  No city data recorded yet
                </div>
              )}
            </div>
          </div>

          {/* Source-wise Leads Chart */}
          <div>
            <h4 style={{ fontSize: '13px', fontWeight: 700, margin: '0 0 12px 0', color: 'var(--text-primary)' }}>
              Source-Wise Leads Breakdown
            </h4>
            <div style={{ height: '220px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {chartsData.crmSourceData?.some(d => d.value > 0) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartsData.crmSourceData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={75}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {chartsData.crmSourceData.map((entry, index) => {
                        const colors = ['#a8080c', '#0284c7', '#10b981', '#f59e0b', '#d97706'];
                        return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                      })}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      iconType="circle"
                      iconSize={8}
                      formatter={(value) => <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 500 }}>{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>No lead sources recorded yet</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 2. Charts Section */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: '20px', marginBottom: '24px' }}>
        {/* Uploads Trend */}
        <div className="card">
          <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>Property Upload Trend</h3>
          <div style={{ height: '240px' }}>
            {chartsData.uploadTrend?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartsData.uploadTrend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorProp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="var(--primary)" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" vertical={false} />
                  <XAxis dataKey="name" stroke="var(--text-muted)" fontSize={11} tickLine={false} dy={8} />
                  <YAxis stroke="var(--text-muted)" fontSize={11} tickLine={false} dx={-4} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="Properties" stroke="var(--primary)" fillOpacity={1} fill="url(#colorProp)" strokeWidth={3} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>No data available</div>
            )}
          </div>
        </div>

        {/* City Wise Properties */}
        <div className="card">
          <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>Top Performing Cities</h3>
          <div style={{ height: '240px' }}>
            {chartsData.cityData?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartsData.cityData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorBar" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--primary)" />
                      <stop offset="100%" stopColor="#e11d48" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" vertical={false} />
                  <XAxis dataKey="name" stroke="var(--text-muted)" fontSize={11} tickLine={false} dy={8} />
                  <YAxis stroke="var(--text-muted)" fontSize={11} tickLine={false} dx={-4} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="properties" name="Properties" fill="url(#colorBar)" barSize={24} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>No data available</div>
            )}
          </div>
        </div>

        {/* Approval Ratio (Pie) */}
        <div className="card">
          <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>Approval Ratio</h3>
          <div style={{ height: '240px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {chartsData.approvalData?.some(d => d.value > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartsData.approvalData}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={88}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {chartsData.approvalData.map((entry, index) => {
                      let color = 'var(--primary)';
                      if (entry.name === 'Approved') color = 'var(--success)';
                      else if (entry.name === 'Pending') color = 'var(--warning)';
                      else if (entry.name === 'Needs Revision') color = 'var(--danger)';
                      return <Cell key={`cell-${index}`} fill={color} />;
                    })}
                  </Pie>
                  <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '20px', fontWeight: 700, fill: 'var(--text-primary)' }}>
                    {stats.totalProperties}
                  </text>
                  <text x="50%" y="56%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '11px', fill: 'var(--text-secondary)', fontWeight: 500 }}>
                    Total Listings
                  </text>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    iconType="circle"
                    iconSize={8}
                    formatter={(value) => <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 500 }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ color: 'var(--text-muted)' }}>No properties in database</div>
            )}
          </div>
        </div>
      </div>

      {/* 3. Lower Grid: Pending Properties + Timeline */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: '20px' }}>
        {/* Pending approvals */}
        <div className="card" style={{ flex: 2 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 600 }}>Properties Pending Approval</h3>
            <Link to="/properties?filter=pending" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--primary)', display: 'flex', alignItems: 'center' }}>
              View All <ChevronRight size={14} />
            </Link>
          </div>

          <div style={{ overflowX: 'auto' }}>
            {pendingProperties.length === 0 ? (
              <div style={{ padding: '36px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                All clear! No properties pending moderation.
              </div>
            ) : (
              <table className="premium-table" style={{ fontSize: '12px' }}>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>City</th>
                    <th>Price</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingProperties.map((prop) => (
                    <tr key={prop.id}>
                      <td style={{ fontWeight: 600 }}>
                        <Link to={`/properties/${prop.id}`} style={{ color: 'var(--primary)', hover: { textDecoration: 'underline' } }}>
                          {prop.title}
                        </Link>
                      </td>
                      <td>{prop.city}</td>
                      <td>{formatIndianCurrency(prop.price)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => handleQuickApprove(prop.id, prop.title)}
                            className="btn btn-primary btn-sm"
                            style={{ padding: '4px 8px', borderRadius: '4px' }}
                          >
                            Approve
                          </button>
                          <Link
                            to={`/properties/${prop.id}`}
                            className="btn btn-secondary btn-sm"
                            style={{ padding: '4px 8px', borderRadius: '4px' }}
                          >
                            Review
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Audit Logs Timeline */}
        <div className="card" style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 600 }}>Recent Activities</h3>
            <Link to="/logs" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--primary)', display: 'flex', alignItems: 'center' }}>
              View Logs <ChevronRight size={14} />
            </Link>
          </div>

          <div className="timeline">
            {recentLogs.length === 0 ? (
              <div style={{ padding: '36px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                No recent actions recorded.
              </div>
            ) : (
              recentLogs.map((log) => (
                <div key={log.id} className="timeline-item">
                  <div className="timeline-marker">
                    <Activity size={14} color="var(--primary)" />
                  </div>
                  <div className="timeline-content">
                    <p style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: 500 }}>
                      <strong style={{ fontWeight: 600 }}>{log.admin_name}</strong>: {log.action} ({log.target || 'System'})
                    </p>
                    <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                      {log.details}
                    </p>
                    <div className="timeline-time">
                      {new Date(log.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={executeConfirmAction}
        title={confirmTitle}
        message={confirmMessage}
        type={confirmType}
        isLoading={loading}
        confirmText="Proceed"
      />
    </div>
  );
};

export default Dashboard;
